import React, {useContext, useEffect, useState} from "react";
import {Loader} from "rsuite";
import AureliaContext from "../utilities/aurelia-context";
import nl2br from "../value-converter/nl2br";

export default function ReferenceDisplay(
    {
        reference,
        children,
        multiModel = false,
        separator = ", ",
        renderAsLink = true,
        labelProperty,
        maximum = 6,
    }
) {
    const {modelLabelService} = useContext(AureliaContext);
    const [links, setLinks] = useState([]);
    const [additionalReferences, setAdditionalReferences] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        if (!reference) {
            setLinks([]);
            setAdditionalReferences([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        let references = Array.isArray(reference) ? reference : [reference];
        let extra = [];

        if (maximum) {
            extra = references.slice(maximum);
            references = references.slice(0, maximum);
        }

        const linkPromises = references.map(
            ref => modelLabelService.getReferenceLinks(ref, multiModel, labelProperty)
        )

        Promise.all(linkPromises).then(resolved => {
            if (!isMounted) {
                return
            }

            setLinks(resolved.filter(link => link))
            setAdditionalReferences(extra)
            setLoading(false)
        })

        return () => {
            isMounted = false
        }
    }, [reference, multiModel, separator, renderAsLink, labelProperty, maximum, modelLabelService])

    function clickMaximum() {
        const newPromises = additionalReferences.map((ref) =>
            modelLabelService.getReferenceLinks(ref, multiModel, labelProperty)
        )

        setAdditionalReferences([])

        Promise.all(newPromises).then((resolved) => {
            setLinks((current) => [...current, ...resolved.filter(link => link)])
        })
    }

    // While loading, keep custom children visible immediately; otherwise show
    // a small spinner in their place.
    if (loading) {
        return children ? children : <Loader size="xs"/>
    }

    // Children supplied but nothing resolved: render the children on their own.
    if (children && links.length === 0) {
        return children
    }

    return (
        <>
            {links.map((link, index) => {
                // Children replace the resolved label as the link/span content.
                const content = children
                    ? {children}
                    : {dangerouslySetInnerHTML: {__html: nl2br(link.label)}}

                return (
                    <React.Fragment key={index}>
                        {link.linkParams && renderAsLink ? (
                            <a
                                href={`/view/${link.linkParams.moduleId}/${link.linkParams.viewId}?id=${link.linkParams.id}`}
                                title={link.longLabel ?? ""}
                                {...content}
                            />
                        ) : (
                            <span title={link.longLabel ?? ""} {...content} />
                        )}
                        {index !== links.length - 1 ? (
                            <span dangerouslySetInnerHTML={{__html: separator}}/>
                        ) : null}
                    </React.Fragment>
                )
            })}
            {0 >= additionalReferences.length ? null : (
                <span role="button" onClick={clickMaximum}>
                    {" + " + additionalReferences.length}
                </span>
            )}
        </>
    )
}
